import type { ReactNode } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from './dragModifiers';
import { DragHandleIcon } from './icons';

interface Item {
  id: string;
}

interface Props<T extends Item> {
  /** Distinguishes this list's drag context from its neighbours'. */
  listId: string;
  items: T[];
  onReorder: (next: T[]) => void;
  /** How the handle names an item: "Drag to reorder {name}". */
  itemName: (item: T, index: number) => string;
  renderItem: (item: T, index: number) => ReactNode;
}

const SortableSettingsRow = ({
  id,
  name,
  children,
}: {
  id: string;
  name: string;
  children: ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <li
      ref={setNodeRef}
      className="settings-list__row"
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
      }}
    >
      <button
        type="button"
        className="roster-drag-handle"
        aria-label={`Drag to reorder ${name}`}
        {...attributes}
        {...listeners}
      >
        <DragHandleIcon />
      </button>
      {children}
    </li>
  );
};

/**
 * An ordered list in Configure roster (evaluation traits, custom indicators) that sorts by drag,
 * with the keyboard sensor so Space, the arrows and Space again do the same as a mouse.
 */
export const SortableSettingsList = <T extends Item>({
  listId,
  items,
  onReorder,
  itemName,
  renderItem,
}: Props<T>) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = items.findIndex((i) => i.id === active.id);
    const to = items.findIndex((i) => i.id === over.id);
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(items, from, to));
  };

  return (
    <DndContext
      id={listId}
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <ol className="settings-list">
          {items.map((item, index) => (
            <SortableSettingsRow key={item.id} id={item.id} name={itemName(item, index)}>
              {renderItem(item, index)}
            </SortableSettingsRow>
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  );
};
